import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, debounce } from 'lodash';
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexBox,
  Typography,
  AbsoluteBox,
  InputField,
  Grid,
  CircularProgress,
  MuiBox,
  Icon,
  textSize,
  color
} from '@kis-technologies/kis-ui';
import { selectors as cartSelectors, Cart, Item } from 'services/cart';
import {
  selectors as configSelectors,
  Configuration,
  ConfigInputField,
  SurveyField,
  LocalizedStrRecord,
  CartDependencies
} from 'services/configuration';
import { actions as customerActions, selectors as customerSelectors } from 'services/customer';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { Keyboard } from 'components/Keyboard';
import { NumPadAction } from 'components/NumPad';
import { Scrollbar } from 'components/Scrollbar';
import { Footer } from 'components/Footer';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { PhoneNumberInput, Country } from 'components/PhoneNumberInput';
import { SelectDropdown } from 'components/SelectDropdown';
import { validateInput, validateInputType } from 'utils/validation';
import { useStyles } from './styled';

const CustomerView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locationRoute = useLocation();
  const state = locationRoute.state as { from?: string };

  // Action Creator and Selectors
  const { getCustomer, saveCustomer, getLocations, resetLocations, resetError, setPhoneCountry } = useActions({
    getCustomer: customerActions.getCustomer,
    saveCustomer: customerActions.saveCustomer,
    getLocations: customerActions.getLocations,
    resetLocations: customerActions.resetLocations,
    resetError: customerActions.resetError,
    setPhoneCountry: customerActions.setPhoneCountry
  });

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const loading = useSelector(customerSelectors.selectLoading);
  const locations = useSelector(customerSelectors.selectLocations);
  const customerError = useSelector(customerSelectors.selectError);
  const customer = useSelector(customerSelectors.selectCustomer);
  const lookupData = useSelector(customerSelectors.selectLookupData);
  const phoneCountry = useSelector(customerSelectors.selectPhoneCountry);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const donationProductInCart = useSelector(cartSelectors.selectDonationProductInCart);
  const lookupConfig = get(config, `${ticketingPath}.customerIdentification.lookup`);
  const lookupEnabled = get(lookupConfig, 'enabled', false);
  const lookupList = get(lookupConfig, 'lookupList', []);
  const notFoundEnabled = get(lookupConfig, 'notFound.enabled', false);
  const notFoundHeader = get(lookupConfig, `notFound.messages.header.${i18n.language}`, t('customer.notFoundHeader'));
  const notFoundSubheader = get(lookupConfig, `notFound.messages.subheader.${i18n.language}`);
  const creationConfig = get(config, `${ticketingPath}.customerIdentification.creation`);
  const priorityCountries: string[] = get(config, `${ticketingPath}.priorityPostalCodeLookupCountries`, []);
  const creationEnabled = get(creationConfig, 'enabled', false);
  const creationList = get(creationConfig, 'creationList', []);
  const emailSuggestions: string[] = get(config, `messaging.${i18n.language}.emailSuggestions.buttons`, [
    '@gmail.com',
    '@yahoo.com',
    '@outlook.com'
  ]);

  // SET INITIAL OBJECTS

  const [formData, setFormData] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<Record<string, string>>({});
  const formRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [displayFields, setDisplayFields] = React.useState<ConfigInputField[]>([]);
  const [inputControl, setInputControl] = React.useState('');
  const [apiKey, setAPIKey] = React.useState('');
  const [selectedField, setSelectedField] = React.useState<ConfigInputField | null>(null);
  const [inputIndex, setInputIndex] = React.useState(-1);
  const [prefilledFieldIndexes, setPrefilledFieldIndexes] = React.useState<number[]>([]);
  const [validation, setValidation] = React.useState(false);
  const [hideScrollBar, setHideScrollBar] = React.useState(false);
  const [skipAndContinue, setSkipAndContinue] = React.useState(true);
  const [emptyLookup, setEmptyLookup] = React.useState(true);
  const [screen, setScreen] = React.useState<'selection' | 'lookup' | 'creation'>('lookup');
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [uiLoading, setUILoading] = React.useState(true);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [phoneNumberInput, setPhoneNumberInput] = React.useState<{ keyboard: string; selectedCountry?: Country }>({
    keyboard: '',
    selectedCountry: phoneCountry
  });
  const [header, setHeader] = React.useState('');
  const [subheader, setSubheader] = React.useState('');
  const [countryOptions, setCountryOptions] = React.useState<string[]>([]);
  const [stateOptions, setStateOptions] = React.useState<string[]>([]);
  const [cityOptions, setCityOptions] = React.useState<string[]>([]);
  const [selectedCountryOption, setSelectedCountryOption] = React.useState<string>('');
  const [selectedStateOption, setSelectedStateOption] = React.useState<string>('');
  const [selectedCityOption, setSelectedCityOption] = React.useState<string>('');
  const [locationFieldsDisabled, setLocationFieldsDisabled] = React.useState<boolean>(true);

  const canSkipAndContinue = () => {
    if (formData && Object.keys(formData).length) {
      let nonEmptyFields = 0;
      let emptyFields = 0;
      let emptyRequiredFields = 0;
      displayFields.forEach((item) => {
        if (formData[item.apiParam]) {
          nonEmptyFields++;
        } else {
          emptyFields++;
          if (item.required) emptyRequiredFields++;
        }
      });

      if (!emptyRequiredFields) {
        if (skipAndContinue) {
          setEmptyLookup(!!!nonEmptyFields);
          if (!!!nonEmptyFields) setValidation(true);
        }
        if (screen === 'creation' && prefilledFieldIndexes.length === displayFields.length) {
          validateForm(!!!emptyFields);
        }
      }
    }
  };

  const canSkipCustomerLookup = () => {
    let skipScreen = false;
    if (
      ((!lookupEnabled || !lookupList || !lookupList.length) &&
        (!creationEnabled || !creationList || !creationList.length)) ||
      customer
    ) {
      skipScreen = true;
    }

    return skipScreen;
  };

  /**
   * Returns the cart items of dependency, if any
   * @param {Object} cartDependencies - cart dependency config
   */
  const getDependencyCartItems = (cartDependencies?: { event?: string[]; genre?: string[]; package?: string[] }) => {
    const cartItems: Item[] = [];
    if (
      cart &&
      cart.products &&
      cartDependencies &&
      ((cartDependencies.genre && cartDependencies.genre.length) ||
        (cartDependencies.event && cartDependencies.event.length) ||
        (cartDependencies.package && cartDependencies.package.length))
    ) {
      cart.products.forEach((product) => {
        if (product.items) {
          if (
            (cartDependencies.genre && cartDependencies.genre.includes(product.genre || '')) ||
            (cartDependencies.event && cartDependencies.event.includes(product.name || ''))
          ) {
            cartItems.push(...product.items);
          }
        } else if (product.subProducts) {
          if (cartDependencies.package && cartDependencies.package.includes(product.name || '')) {
            if (product.subProducts[0] && product.subProducts[0].items) {
              cartItems.push(...product.subProducts[0].items);
            }
          } else {
            product.subProducts.forEach((subProduct) => {
              if (subProduct.items) {
                if (
                  (cartDependencies.genre && cartDependencies.genre.includes(subProduct.genre || '')) ||
                  (cartDependencies.event && cartDependencies.event.includes(subProduct.name || ''))
                ) {
                  cartItems.push(...subProduct.items);
                }
              }
            });
          }
        }
      });
    }

    return cartItems;
  };

  const getReplacedHeader = (labelObj: Partial<Record<string, string>> = {}, cartItem: Item) => {
    const replacedLabelObj = { ...labelObj };
    let labelTxt = get(replacedLabelObj, `${i18n.language}`) || '';
    const cartItemProduct = cart.products.find(
      (product) => product.items && product.items.find((item) => item.id === cartItem.id)
    );
    labelTxt = labelTxt.replace('{{performanceName}}', (cartItemProduct && cartItemProduct.name) || '');
    labelTxt = labelTxt.replace('{{priceTypeName}}', cartItem.type);
    replacedLabelObj[i18n.language] = labelTxt;

    return replacedLabelObj;
  };

  const getSubFieldsToDisplay = (subFields: SurveyField[], cartItems: Item[], surveyApiParam: string) => {
    const items: SurveyField[] = [];
    const filteredFields = subFields.filter((sf) => sf.surveyApiParam === surveyApiParam);
    cartItems.forEach((cartItem, cartIdx) => {
      filteredFields.forEach((filteredItem) => {
        if (filteredItem.inputType === 'header') {
          const headerText = getReplacedHeader(filteredItem.label, cartItem);
          items.push({ ...filteredItem, label: headerText, apiParam: filteredItem.apiParam + cartIdx });
        } else {
          items.push({ ...filteredItem, apiParam: filteredItem.apiParam + cartIdx });
        }
      });
    });

    return items;
  };

  /**
   * Set the displayFields for customer config items,
   * checking cart dependencies if any
   */
  const mapDisplayFields = (configList: ConfigInputField[], headerText: string = '') => {
    let tempFields: ConfigInputField[] = [];
    configList.every((entryConfig) => {
      const configFields: ConfigInputField[] = get(entryConfig, `fields`, []);
      const configSubFields: SurveyField[] = get(entryConfig, `subFields`, []);
      const cartDependencies: CartDependencies = get(entryConfig, 'cartDependencies', {});

      // form with a dependency of an event, genre or package
      if (cartDependencies.event || cartDependencies.genre || cartDependencies.package) {
        const dependencyCartItems = getDependencyCartItems(cartDependencies);
        if (dependencyCartItems && dependencyCartItems.length) {
          configFields.forEach((configItem) => {
            if (configItem.inputType === 'sub-survey') {
              tempFields = tempFields.concat(
                getSubFieldsToDisplay(configSubFields, dependencyCartItems, configItem.apiParam)
              );
            } else {
              tempFields.push(configItem);
            }
          });
        }
        // form with no dependency, OR a dependency of a donation
      } else if (Object.keys(cartDependencies).length < 1 || donationProductInCart) {
        configFields.forEach((configItem) => {
          if (configItem.inputType === 'sub-survey') {
            let subFields: SurveyField[] = [];
            cart.products.forEach((product) => {
              if (product.subProducts && product.subProducts[0] && product.subProducts[0].items) {
                subFields = subFields.concat(
                  getSubFieldsToDisplay(configSubFields, product.subProducts[0].items, configItem.apiParam)
                );
              } else if (product.items) {
                subFields = subFields.concat(
                  getSubFieldsToDisplay(configSubFields, product.items, configItem.apiParam)
                );
              }
            });
            tempFields = tempFields.concat(subFields);
          } else {
            tempFields.push(configItem);
          }
        });
      }

      // Stop iterating the configList when displayFields are found
      if (tempFields.length) {
        setHeader(get(entryConfig, `messages.header.${i18n.language}`, headerText));
        setSubheader(get(entryConfig, `messages.subheader.${i18n.language}`, ''));
        setDisplayFields(tempFields);
        return false;
      }
      return true;
    });

    // no fields to show; navigate forward or backward
    if (!tempFields || !tempFields.length) {
      if (state && state.from === '/ticketing/cart/survey') {
        onPrevPageClick();
      } else {
        if (screen === 'lookup') setScreen('creation');
        else navigate('/ticketing/cart/survey');
      }
    }
  };

  const nextText = skipAndContinue && emptyLookup ? t('commonText.skip') : t('commonText.continue');
  const classes = useStyles({ inputControl })();

  // Reset data when the component mounts or re-renders
  useEffect(() => {
    resetLocations();
  }, []);

  // Navigation for skipping this page
  useEffect(() => {
    if (canSkipCustomerLookup()) {
      if (state && state.from === '/ticketing/cart/survey') {
        onPrevPageClick();
      } else {
        navigate('/ticketing/cart/survey');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, navigate]);

  // Navigating forward after customer is found/created
  useEffect(() => {
    if (customer && !emptyLookup) {
      navigate('/ticketing/cart/survey');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    if (screen === 'lookup') {
      if (lookupEnabled && lookupList && lookupList.length) {
        mapDisplayFields(lookupList, t('customer.lookupHeader'));
      } else {
        setScreen('creation');
      }
    } else if (screen === 'creation') {
      if (creationEnabled && creationList && creationList.length) {
        mapDisplayFields(creationList, t('customer.creationHeader'));
      } else {
        navigate('/ticketing/cart/survey');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  useEffect(() => {
    setValidation(false);
    const apiParams = displayFields.map((item) => {
      if (skipAndContinue && item.required) {
        setSkipAndContinue(false);
      }
      return item.apiParam;
    });

    const initialFormRefs: Record<string, HTMLInputElement | null> = {};
    formRefs.current = apiParams.reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, initialFormRefs);

    const initialFormData: Record<string, string> = {};
    const tempRef = apiParams.reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, initialFormData);

    setError({ ...tempRef });

    // Prefill the customer creation screen with data from lookup
    const prefilledIndexes: number[] = [];
    Object.keys(tempRef).forEach((formField, idx) => {
      if (screen === 'creation' && lookupData && lookupData[formField]) {
        tempRef[formField] = lookupData[formField] || '';
        prefilledIndexes.push(idx);
      }
    });
    if (tempRef && tempRef.phone && phoneCountry) {
      const phoneNumber = tempRef.phone.split(phoneCountry.code)[1];
      if (phoneNumber) {
        setPhoneNumberInput({ keyboard: phoneNumber, selectedCountry: phoneCountry });
      }
    }
    // Save the indexes, used to disable those fields
    if (prefilledIndexes && prefilledIndexes.length) {
      setPrefilledFieldIndexes(prefilledIndexes);
    }

    setFormData(tempRef);
    resetError();

    // If there is only one field to display, make input focused and keyboard displayed
    if (displayFields && displayFields.length === 1) {
      setAPIKey(displayFields[0].apiParam);
      setSelectedField(displayFields[0]);
      setInputIndex(0);
    }

    if (displayFields && displayFields.length > 0) {
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayFields]);

  useEffect(() => {
    // Check if apiKey is valid, and prevent the effect from running if it's not.
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
      return;
    }

    // Get the actual HTMLInputElement from formRefs.current
    const selectedInput = formRefs.current[apiKey];
    // Only proceed if the selected input element actually exists (is not null)
    if (selectedInput) {
      setTimeout(() => {
        if (inputControl) {
          selectedInput.focus();
          if (selectedInput.type === 'text') {
            selectedInput.setSelectionRange(selectedInput.value.length, selectedInput.value.length, 'forward');
          }
        }

        const elementToScroll = selectedInput.lastElementChild || selectedInput;
        elementToScroll.scrollIntoView({ block: 'center' });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIndex, inputControl, apiKey]);

  useEffect(() => {
    if (error[apiKey]) {
      setError({ ...error, [apiKey]: '' });
      const currIndex = displayFields.findIndex((item) => item.apiParam === apiKey);
      validate(currIndex);
    }

    if (skipAndContinue || (screen === 'creation' && prefilledFieldIndexes.length === displayFields.length)) {
      canSkipAndContinue();
    }

    if (locations && selectedField && selectedField.apiParam === 'postalCode') {
      resetLocations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (selectedField) {
      setTimeout(() => {
        handleKeyBoardVisibility(selectedField.keyboard);
      }, 250);
    }
  }, [selectedField]);

  useEffect(() => {
    if (customerError) {
      if (locationsLoading) {
        setLocationsLoading(false);
        setLocationFieldsDisabled(false);
      } else if (screen === 'lookup' && notFoundEnabled) {
        setShowOverlay(true);
        setUILoading(false);
      } else if (screen === 'lookup' && !notFoundEnabled) {
        setScreen('creation');
      }

      resetError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerError]);

  // Handle initial population of country options
  useEffect(() => {
    setLocationsLoading(false);
    setLocationFieldsDisabled(true);

    if (locations && locations.length === 0) {
      setError({ ...error, postalCode: t('validation.postalCodeNotFound') });
      setValidation(false);
    } else if (locations && locations.length > 0) {
      // Populate country options
      const uniqueCountries = [...new Set(locations.map((location) => location.country))];
      const sortedCountries = priorityCountries.length
        ? uniqueCountries.sort((countryA, countryB) => {
          const priorityCountryA = priorityCountries.indexOf(countryA);
          const priorityCountryB = priorityCountries.indexOf(countryB);

          if (priorityCountryA === -1) return 1;
          if (priorityCountryB === -1) return -1;

          return priorityCountryA - priorityCountryB;
        })
        : uniqueCountries;
      setCountryOptions(sortedCountries);

      // Set default country if options are available
      if (uniqueCountries.length > 0) {
        setAPIKey('country');
        const defaultCountry = uniqueCountries[0];
        setFormValueIfKeyExists(defaultCountry, 'country');
        setSelectedCountryOption(defaultCountry);
      }
    }
  }, [locations]);

  // Update states, or cities if 0 states, based on selected country
  useEffect(() => {
    if (selectedCountryOption && locations) {
      // Filter states based on selected country
      const filteredStates = [
        ...new Set(
          locations.filter((location) => location.country === selectedCountryOption).map((location) => location.state)
        )
      ];
      setAPIKey('state');
      setStateOptions(filteredStates);
      const defaultState = filteredStates[0] || '';
      setSelectedStateOption(defaultState);

      let defaultCity = '';
      if (!defaultState) {
        // Update cities based on the selected country if there is no state
        const filteredCities = [
          ...new Set(
            locations.filter((location) => location.country === selectedCountryOption).map((location) => location.city)
          )
        ];
        setAPIKey('city');
        setCityOptions(filteredCities);
        defaultCity = filteredCities[0] || '';
        setSelectedCityOption(defaultCity);
      }

      // update the form values, only if the keys exist on the form
      const updatedFormData: any = {};
      if (Object.keys(formData).includes('state')) updatedFormData.state = defaultState;
      if (Object.keys(formData).includes('city')) updatedFormData.city = defaultCity;
      setFormData({ ...formData, ...updatedFormData });
    }
  }, [selectedCountryOption]);

  // Update cities based on selected state
  useEffect(() => {
    if (selectedStateOption && locations) {
      // Update cities based on the selected state
      const filteredCities = [
        ...new Set(
          locations.filter((location) => location.state === selectedStateOption).map((location) => location.city)
        )
      ];
      setAPIKey('city');
      setCityOptions(filteredCities);
      const defaultCity = filteredCities[0] || '';
      setFormValueIfKeyExists(defaultCity, 'city');
      setSelectedCityOption(defaultCity);
    }
  }, [selectedStateOption]);

  useEffect(() => {
    if (selectedCityOption && locations) {
      validateForm();
    }
  }, [selectedCityOption]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // Handles the keyboard key click -- changes input accordingly
  const onClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (selectedField && selectedField.inputType === 'phone') {
      if (v === 'delete') {
        setPhoneNumberInput((prevState) => ({
          ...prevState,
          keyboard: prevState.keyboard.slice(0, prevState.keyboard.length - 1)
        }));
      } else if (v === 'clear') {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: '' }));
      } else {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: prevState.keyboard + v }));
      }
    } else if (v === 'delete') {
      setFormData({ ...formData, [apiKey]: formData[apiKey].slice(0, formData[apiKey].length - 1) });
    } else if (v === 'clear') {
      setFormData({ ...formData, [apiKey]: '' });
    } else {
      if (selectedField && selectedField.maxChar) {
        if (selectedField.maxChar > formData[apiKey].length) {
          setFormData({ ...formData, [apiKey]: formData[apiKey] + v });
        }
      } else {
        setFormData({ ...formData, [apiKey]: formData[apiKey] + v });
      }
    }
  };

  const onSuggestionClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (selectedField && selectedField.keyboard === 'country') {
      setFormData({ ...formData, [apiKey]: v });
    } else {
      setFormData({ ...formData, [apiKey]: formData[apiKey] + v });
    }
  };

  // Handle which input ref has the focus
  const handleFocus = (item: ConfigInputField, index: number) => {
    setSelectedField(item);
    setAPIKey(item.apiParam);
    setInputIndex(index);
  };

  const debouncedGetLocations = debounce((postalCode: string) => {
    getLocations({ postalCode });
  }, 300); // Adjust the debounce delay as needed

  const handleBlur = (item: ConfigInputField) => {
    setSelectedField(null);
    handleControlsHide();
    // Clear previous data and states
    setCountryOptions([]);
    setStateOptions([]);
    setCityOptions([]);
    setSelectedCountryOption('');
    setSelectedStateOption('');
    setSelectedCityOption('');
    setLocationsLoading(false);

    // reset the form values, only if the keys exist on the form
    const updatedFormData: any = {};
    if (Object.keys(formData).includes('country')) updatedFormData.country = '';
    if (Object.keys(formData).includes('state')) updatedFormData.state = '';
    if (Object.keys(formData).includes('city')) updatedFormData.city = '';
    setFormData({ ...formData, ...updatedFormData });

    // validate the input value and call getLocations
    if (formData[item.apiParam]) {
      const validateInputError = validateInput(item, formData);
      const validateInputTypeError = validateInputType(formData[item.apiParam], item.inputType || '');

      if (!validateInputError && !validateInputTypeError) {
        setLocationsLoading(true);
        debouncedGetLocations(formData[item.apiParam]);
      }
    }
  };

  const handleControlsHide = () => {
    setTimeout(() => {
      setHideScrollBar(false);
      setInputControl('');
      validateForm();
    }, 0);
  };

  const isValidInput = (item: string, type: string, key: string) => {
    if (type === 'phone') {
      const { keyboard, selectedCountry } = phoneNumberInput;
      if (keyboard && selectedCountry && keyboard.length < selectedCountry.size) {
        setError((prevState) => ({
          ...prevState,
          [key]: i18n.t('validation.phoneMinDigitsError', { phoneMinDigits: selectedCountry && selectedCountry.size })
        }));
        setValidation(false);
        return false;
      }
    } else {
      const errorMsg = validateInputType(item, type);
      if (errorMsg) {
        setError((prevState) => ({ ...prevState, [key]: errorMsg }));
        setValidation(false);
        return false;
      }
    }

    return true;
  };

  const validate = (currIndex: number) => {
    const currItem = displayFields[currIndex];
    const errorStr = validateInput(currItem, formData);
    if (errorStr) {
      setError((prevState) => ({ ...prevState, [currItem.apiParam]: errorStr }));
      setValidation(false);
      return false;
    } else if (formData[currItem.apiParam] && currItem.inputType) {
      return isValidInput(formData[currItem.apiParam], currItem.inputType, currItem.apiParam);
    }
    return true;
  };

  const validateForm = (canTriggerNextPage?: boolean) => {
    let count = 0;
    displayFields.forEach((_item, index) => {
      const valid = validate(index);
      if (valid) count++;
      if (count === displayFields.length) {
        setValidation(true);
        if (canTriggerNextPage) {
          onNextPageClick(true);
        }
      } else {
        setValidation(false);
      }
    });
  };

  /**
   * Handles navigation from the keyboard, switching between fields
   * or hiding the keyboard if no more fields are available.
   */
  const onKeyboardNavigationClick = (index: number, direction: string) => {
    // validate the current field
    validate(index);

    // get the new field to focus, whether it is next or back
    let newDisplayFieldIndex;
    if (direction === 'next') {
      newDisplayFieldIndex = getNextActiveDisplayFieldIndex(index);
    } else {
      newDisplayFieldIndex = getPreviousActiveDisplayFieldIndex(index);
    }
    const newDisplayField = displayFields[newDisplayFieldIndex];

    if (newDisplayField && formRefs.current[newDisplayField.apiParam]) {
      // if another displayField: change focus to it
      setAPIKey(newDisplayField.apiParam);
      setSelectedField(newDisplayField);
      setInputIndex(newDisplayFieldIndex);
    } else {
      // else: hide the keyboard
      handleControlsHide();
      if (displayFields.length === 1 && direction === 'next') {
        validateForm(true);
      }
    }
  };

  const handleKeyBoardVisibility = (type = 'alphaNumeric') => {
    if (type === 'numeric' || type === 'date') {
      setInputControl('numpad');
    } else {
      setInputControl('keyboard');
    }
    setHideScrollBar(true);
  };

  /**
   * Returns the index of the next active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getNextActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let nextDisplayField;
    do {
      tempIndex++;
      nextDisplayField = displayFields[tempIndex];
    } while (
      tempIndex < displayFields.length &&
      (!formRefs.current[nextDisplayField.apiParam] || formRefs.current[nextDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  /**
   * Returns the index of the previous active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getPreviousActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let prevDisplayField;
    do {
      tempIndex--;
      prevDisplayField = displayFields[tempIndex];
    } while (
      tempIndex > 0 &&
      (!formRefs.current[prevDisplayField.apiParam] || formRefs.current[prevDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  const onPrevPageClick = () => {
    resetLocations();
    navigate('/ticketing/cart', { state: { from: locationRoute.pathname } });
  };

  const onNextPageClick = (skipValidation?: boolean) => {
    resetLocations();
    setUILoading(true);
    // if all fields are not required user can skip and continue the purchase flow
    if (skipAndContinue && emptyLookup) {
      if (screen === 'lookup') {
        setScreen('creation');
      } else {
        navigate('/ticketing/cart/survey');
      }
    } else {
      if (validation || skipValidation) {
        setEmptyLookup(false);
        let payload = {};
        if (screen === 'lookup') {
          payload = getCustomerPayload();
          getCustomer(payload);
        } else {
          payload = getSaveCustomerPayload();
          saveCustomer(payload);
        }
      } else {
        validateForm();
        const errStr = Object.values(error).find((item) => item.length);
        if (errStr) {
          const idx = Object.values(error).findIndex((item) => item === errStr);
          handleFocus(displayFields[idx], idx);
        }
      }
    }
  };

  const getLocalizedText = (textObj: LocalizedStrRecord = {}, apiParam?: string): string => {
    return get(textObj, `${i18n.language}`) || t(`inputFieldLabel.${apiParam}`);
  };

  /**
   * Returns the component to use as the label for an input field.
   *
   * There is an error when passing `label` to InputField if it is a child of InputMask.
   * The workaround is to render the label separately, which requires additional
   * styles to match what is applied to the `label` of other InputField(s).
   */
  const getLabelText = (configInputField: ConfigInputField) => {
    const { required, label, apiParam, inputMask } = configInputField;

    const typographyStyles = inputMask ? { padding: '0 24px', margin: '0 0 10px' } : {};

    return (
      <Typography fontSize={24} fontWeight={600} style={typographyStyles}>
        {required ? <span style={{ color: 'red' }}>* </span> : ''}
        {getLocalizedText(label, apiParam)}
      </Typography>
    );
  };

  const getNextText = (idx: number) => {
    let nextLabel = t('commonText.next');

    if (displayFields.length === 1) {
      nextLabel = nextText;
    } else {
      const nextDisplayFieldIndex = getNextActiveDisplayFieldIndex(idx);
      const nextDisplayField = displayFields[nextDisplayFieldIndex];
      if (!nextDisplayField || !formRefs.current[nextDisplayField.apiParam]) {
        nextLabel = t('commonText.done');
      }
    }

    return nextLabel;
  };

  const getOverlayDescription = () => {
    let description = '';

    if (notFoundSubheader) {
      description = notFoundSubheader;
    } else if (creationEnabled && creationList && creationList.length) {
      description = t('customer.notFoundSubheader');
    } else {
      description = t('customer.notFoundSubheaderAlt');
    }

    return description;
  };

  const getCustomerPayload = () => {
    const payload: { [k: string]: any } = {};
    displayFields.forEach((item) => {
      if (formData[item.apiParam]) {
        payload[item.apiParam] = formData[item.apiParam].trim();
      }
    });

    if (Object.keys(payload).length === displayFields.length) {
      payload.narrowResultsToOne = true;
    }

    return payload;
  };

  const setPhoneNumber = (phoneNumber: string, phNumInp: typeof phoneNumberInput, apiParam: string) => {
    setFormData((prev) => ({
      ...prev,
      [apiParam]: phoneNumber
    }));

    setPhoneNumberInput(phNumInp);
    setPhoneCountry(phNumInp.selectedCountry);
  };

  /**
   * Sets the value of formData[apiParam], if apiParam exists on the form.
   *
   * Example use case is to prevent 'country' from being added to the form
   * when doing a postal code lookup, if that field is not configured.
   */
  const setFormValueIfKeyExists = (value: string, apiParam: string) => {
    if (Object.keys(formData).length && Object.keys(formData).includes(apiParam)) {
      setFormData({ ...formData, [apiParam]: value });
    }
  };

  const handleSelectCountryChange = (value: string, item: ConfigInputField) => {
    setSelectedCountryOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };
  const handleSelectStateChange = (value: string, item: ConfigInputField) => {
    setSelectedStateOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };
  const handleSelectCityChange = (value: string, item: ConfigInputField) => {
    setSelectedCityOption(value);
    setFormValueIfKeyExists(value, item.apiParam);
  };

  const renderStandardTextInput = (field: ConfigInputField, idx: number, isDisabled: boolean) => {
    return (
      <InputField
        size="large"
        fullWidth
        type="text"
        name={field.apiParam}
        label={getLabelText(field)}
        placeholder={getLocalizedText(field.placeholder ? field.placeholder : field.label, field.apiParam)}
        value={formData[field.apiParam]}
        error={Boolean(error[field.apiParam])}
        onFocus={(_e) => {
          if (isDisabled) return;
          handleFocus(field, idx);
        }}
        onClick={(_e) => {
          if (isDisabled) return;
          setInputIndex(idx);
          handleKeyBoardVisibility(field.keyboard);
        }}
        sx={{ height: '80px' }}
        className={classes.customerInputField}
        disabled={isDisabled}
        inputRef={(el: HTMLInputElement | null) => {
          formRefs.current[field.apiParam] = el;
        }}
      />
    );
  };

  const renderPostalLookupFields = (
    field: ConfigInputField,
    fieldIndex: number,
    options: string[],
    onChange: (value: string, item: ConfigInputField) => void
  ) => {
    return (
      <>
        <Box sx={{ position: 'relative' }}>
          {options.length > 1 ? (
            <Grid sx={{ marginTop: '4px' }}>
              <SelectDropdown
                gridProps={{ style: { height: '80px', padding: '0 56px' } }}
                label={getLabelText(field)}
                options={options}
                onChange={onChange}
                item={field}
              />
            </Grid>
          ) : (
            renderStandardTextInput(field, fieldIndex, locationFieldsDisabled)
          )}
          {locationsLoading && renderCircularProgress({ right: '50px', bottom: '22px' })}
        </Box>
        {renderFieldError(field)}
      </>
    );
  };

  const renderCircularProgress = (customStyles: any) => (
    <CircularProgress
      color="inherit"
      size={36}
      style={{
        position: 'absolute',
        color: color.GREEN,
        ...customStyles
      }}
    />
  );

  /**
   * Render a red error message IF the field has an error defined.
   */
  const renderFieldError = (field: ConfigInputField) => (
    <>
      {error[field.apiParam] && error[field.apiParam].length && (
        <Typography color={color.RED} fontSize={textSize.kiosk.SMALL} sx={{ padding: '0 0 0 58px' }}>
          {error[field.apiParam]}
        </Typography>
      )}
    </>
  );

  const renderFormFields = (configInput: ConfigInputField, idx: number) => {
    switch (configInput.inputType) {
      case 'phone':
        return (
          <Box fullWidth>
            <PhoneNumberInput
              ref={(el: HTMLInputElement | null) => {
                formRefs.current[configInput.apiParam] = el;
              }}
              gridProps={{
                style: { height: '80px' }
              }}
              textInputProps={{
                name: 'value',
                placeholder: getLocalizedText(
                  configInput.placeholder ? configInput.placeholder : configInput.label,
                  configInput.apiParam
                ),
                fullWidth: true
              }}
              label={getLabelText(configInput)}
              inputModel={phoneNumberInput}
              setPhoneNumber={(phoneNumber, number) => setPhoneNumber(phoneNumber, number, configInput.apiParam)}
              onFocus={() => handleFocus(configInput, idx)}
              onClick={() => {
                setInputIndex(idx);
                handleKeyBoardVisibility(configInput.keyboard);
              }}
              error={error[configInput.apiParam] || undefined}
            />
          </Box>
        );

      case 'lookupPostalCode':
        return (
          <>
            <Box sx={{ position: 'relative' }}>
              <InputField
                size="large"
                fullWidth
                type="text"
                name={configInput.apiParam}
                label={getLabelText(configInput)}
                placeholder={getLocalizedText(
                  configInput.placeholder ? configInput.placeholder : configInput.label,
                  configInput.apiParam
                )}
                value={formData[configInput.apiParam]}
                error={Boolean(error[configInput.apiParam])}
                onFocus={(_e) => handleFocus(configInput, idx)}
                onBlur={(_e) => handleBlur(configInput)}
                onClick={(_e) => {
                  setInputIndex(idx);
                  handleKeyBoardVisibility(configInput.keyboard);
                }}
                sx={{ height: '80px' }}
                className={classes.customerInputField}
                inputRef={(el: HTMLInputElement | null) => {
                  formRefs.current[configInput.apiParam] = el;
                }}
              />
              {locations && locations.length > 0 && (
                <Icon
                  name="CheckCircleOutline"
                  sx={{
                    color: color.GREEN,
                    fontSize: '36px',
                    position: 'absolute',
                    right: '50px',
                    bottom: '22px'
                  }}
                />
              )}
            </Box>
            {renderFieldError(configInput)}
          </>
        );

      case 'autoCountry':
        return renderPostalLookupFields(configInput, idx, countryOptions, handleSelectCountryChange);

      case 'autoState':
        return renderPostalLookupFields(configInput, idx, stateOptions, handleSelectStateChange);

      case 'autoCity':
        return renderPostalLookupFields(configInput, idx, cityOptions, handleSelectCityChange);

      default:
        return (
          <>
            {configInput.inputMask ? (
              <>
                {getLabelText(configInput)}
                <InputMask
                  mask={configInput.inputMask}
                  value={formData[configInput.apiParam]}
                  onFocus={() => handleFocus(configInput, idx)}
                  name={configInput.apiParam}
                  maskPlaceholder={getLocalizedText(
                    configInput.placeholder ? configInput.placeholder : configInput.label,
                    configInput.apiParam
                  )}
                  onClick={() => {
                    setInputIndex(idx);
                    handleKeyBoardVisibility(configInput.keyboard);
                  }}
                >
                  <InputField
                    size="large"
                    fullWidth
                    type="text"
                    inputProps={{ maxLength: 8 }}
                    placeholder={getLocalizedText(
                      configInput.placeholder ? configInput.placeholder : configInput.label,
                      configInput.apiParam
                    )}
                    error={Boolean(error[configInput.apiParam])}
                    sx={{ height: '80px' }}
                    className={classes.customerInputField}
                    inputRef={(el: HTMLInputElement | null) => {
                      formRefs.current[configInput.apiParam] = el;
                    }}
                  />
                </InputMask>
              </>
            ) : (
              renderStandardTextInput(configInput, idx, false)
            )}
            {renderFieldError(configInput)}
          </>
        );
    }
  };

  const getSaveCustomerPayload = () => {
    const payload: { [k: string]: any } = {};
    displayFields.forEach((item) => {
      if (formData[item.apiParam]) {
        payload[item.apiParam] = formData[item.apiParam].trim();
      }
    });

    return payload;
  };

  const getSuggestions = () => {
    let result;
    if (selectedField && selectedField.keyboard === 'email') {
      result = emailSuggestions;
    }

    return result;
  };

  if (uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      {showOverlay && !loading && (
        <ActionOverlay
          height={600}
          title={
            <Box margin="0 0 5" width={720}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {notFoundHeader}
              </Typography>
            </Box>
          }
          description={getOverlayDescription()}
          confirmText={t('commonText.skip')}
          cancelText={t('commonText.tryAgain')}
          onConfirm={() => {
            setScreen('creation');
            setShowOverlay(false);
          }}
          onCancel={() => {
            setShowOverlay(false);
          }}
        />
      )}
      <Grid container alignContent="space-between" className={classes.customerViewOuterContainer}>
        <Grid container item alignContent="space-between" className={classes.customerViewInnerContainer}>
          {displayFields && displayFields.length ? (
            <>
              <Box width="100%" flexOverride="none" margin="0 0 6">
                <Typography fontSize={64} fontWeight={600} align="center">
                  {header}
                </Typography>
                <Typography fontSize={30} align="center">
                  {subheader}
                </Typography>
              </Box>
              <MuiBox className={classes.customerViewInputsBox} height={subheader ? '500px' : '600px'}>
                <Scrollbar hideScrollBar={hideScrollBar} refreshModel={screen}>
                  <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                    <Grid container alignContent="flex-start" className={classes.customerViewInputsContainer}>
                      {screen &&
                        displayFields &&
                        displayFields.map((configInput, idx) => (
                          <Grid
                            key={idx}
                            item
                            xs={configInput.halfWidth ? 6 : 12}
                            sx={
                              configInput.inputType === 'header' ? { padding: '0px 20px 0' } : { padding: '10px 20px' }
                            }
                          >
                            {renderFormFields(configInput, idx)}
                          </Grid>
                        ))}
                    </Grid>
                  </FlexBox>
                </Scrollbar>
              </MuiBox>
            </>
          ) : (
            <FlexBox sx={{ height: '650px' }} justify="center" align="center">
              <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
            </FlexBox>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      {!inputControl && (
        <Footer
          onPrevClick={onPrevPageClick}
          onNextClick={onNextPageClick}
          isNextDisabled={!validation || locationsLoading}
          nextText={nextText}
          skipNavPrompt={showOverlay}
        />
      )}

      {/* Keyboard */}
      {inputControl === 'keyboard' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
          <Grid>
            <Keyboard
              language={i18n.language}
              onClick={onClick}
              onNext={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onPrev={() => onKeyboardNavigationClick(inputIndex, 'back')}
              nextText={getNextText(inputIndex)}
              prevText={inputIndex ? t('commonText.previous') : t('commonText.back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              suggestions={getSuggestions()}
              onSuggestionClick={onSuggestionClick}
            />
          </Grid>
        </AbsoluteBox>
      )}

      {inputControl === 'numpad' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <Grid>
            <NumPadAction
              onClick={onClick}
              onConfirm={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onBack={() => onKeyboardNavigationClick(inputIndex, 'back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              layout="row"
              confirmConfig={{
                label: getNextText(inputIndex),
                backLabel: inputIndex ? t('commonText.previous') : t('commonText.back'),
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          </Grid>
        </AbsoluteBox>
      )}
    </>
  );
};

export default CustomerView;
